import { useCallback, useEffect } from "react";

import { EmptystateHelmetColored } from "components/icons/generated";
import LinkButton from "ds/components/Button/LinkButton";
import { useActiveDrawerId } from "ds/components/DrawerNew/useActiveDrawerId";
import { isPathnameDifferent } from "ds/components/DrawerNew/utils";
import EmptyState from "ds/components/EmptyState";
import TableSimple from "ds/components/TableSimple";
import useAnalytics from "hooks/useAnalytics";
import { AnalyticsPagePolicy } from "hooks/useAnalytics/pages/policy";
import useErrorHandle from "hooks/useErrorHandle";
import useTitle from "hooks/useTitle";
import useTypedContext from "hooks/useTypedContext";
import useURLParams from "hooks/useURLParams";
import { SpacesContext } from "views/Account/SpacesProvider";
import { AccountContext } from "views/AccountWrapper";

import { showPolicyCreateDrawer } from "../../PolicyCreateDrawer";
import PoliciesViewHeader from "../ViewHeader";
import useTakenPolicyNames from "../useTakenPolicyNames";
import PoliciesListFiltersLayout from "./FiltersLayout";
import { POLICIES_TABLE_CELLS } from "./Table/Cells";
import { POLICIES_SORT_CONFIG } from "./Table/constants";
import usePoliciesTableStore from "./Table/useTableStore";
import { FILTERS_ORDER_SETTINGS_KEY, VIRTUALIZATION_THRESHOLD } from "./constants";
import useSearchPolicies from "./useSearchPolicies";
import useSearchPoliciesSuggestions from "./useSearchPoliciesSuggestions";

const PoliciesList = () => {
  const { hasEntityCreateAccess: canCreatePolicy } = useTypedContext(SpacesContext);
  const { accountName } = useTypedContext(AccountContext);
  const urlParams = useURLParams();

  const activeRowId = useActiveDrawerId();
  const trackAnalytics = useAnalytics({ page: AnalyticsPagePolicy.PoliciesList });

  useTitle(`Policies · ${accountName}`);

  const {
    entities,
    error,
    isPageEmpty,
    hasNoFilteringResults,
    loading,
    loadingContent,
    hasNextPage,
    loadMoreItems,
  } = useSearchPolicies();

  const {
    filters,
    filtersMap,
    sortOptions,
    loading: filtersLoading,
    refetchActiveSections,
    filteredCount,
  } = useSearchPoliciesSuggestions();

  const takenPolicyNames = useTakenPolicyNames();

  const openCreateDrawer = useCallback(
    (omitQuerySearchCheck?: boolean) => {
      showPolicyCreateDrawer({
        takenPolicyNames,
        shouldCloseOnLocationChange: omitQuerySearchCheck ? isPathnameDifferent : undefined,
      });
    },
    [takenPolicyNames]
  );

  useEffect(() => {
    if (urlParams.has("create-new")) {
      openCreateDrawer(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const ErrorContent = useErrorHandle(error);

  if (ErrorContent) {
    return ErrorContent;
  }

  return (
    <>
      <PoliciesViewHeader />
      <PoliciesListFiltersLayout
        openCreateDrawer={openCreateDrawer}
        isPageEmpty={isPageEmpty}
        renderedEntitiesCount={entities.length}
        loadingIndication={loading}
        filters={filters}
        filtersMap={filtersMap}
        sortOptions={sortOptions}
        filtersLoading={filtersLoading}
        refetchActiveSections={refetchActiveSections}
        filteredCount={filteredCount}
        canCreatePolicy={canCreatePolicy}
      >
        {isPageEmpty && (
          <EmptyState
            title="You don't have any policies yet"
            icon={EmptystateHelmetColored}
            caption={`Policies are declarative rules evaluated at various decision points within Spacelift.
            Create new policy from scratch using button above or start with one of our templates.`}
          >
            {canCreatePolicy && (
              <LinkButton variant="secondary" to="/policies-templates">
                Explore templates
              </LinkButton>
            )}
          </EmptyState>
        )}
        {hasNoFilteringResults && (
          <EmptyState
            title="No results"
            icon={EmptystateHelmetColored}
            caption="Try different filters."
            announce
          />
        )}
        {(!!entities.length || loadingContent) && (
          <TableSimple
            data={entities}
            loading={loadingContent}
            store={usePoliciesTableStore}
            ariaLabel="Policies list"
            activeRowId={activeRowId}
            analyticsPage={AnalyticsPagePolicy.PoliciesList}
            trackAnalytics={trackAnalytics}
            cellPerColumn={POLICIES_TABLE_CELLS}
            filtersStorageKey={FILTERS_ORDER_SETTINGS_KEY}
            sortConfig={POLICIES_SORT_CONFIG}
            onLoadMore={hasNextPage ? loadMoreItems : undefined}
            virtualize={entities.length >= VIRTUALIZATION_THRESHOLD}
            hasPagination={false}
          />
        )}
      </PoliciesListFiltersLayout>
    </>
  );
};

export default PoliciesList;
